"use client"

import { User } from "lucide-react"
import { forwardRef, useMemo } from "react"
import { observer } from "mobx-react-lite"

import { useStore } from "@/app/components/common/store.provider"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"
import { toProxyUploadUrl } from "@/lib/utils/upload-url"

export type AuthUserButtonColor = "brand" | "white"

type AuthUserButtonProps = {
  /** brand = primary colors; white = all-white for hero overlays */
  color?: AuthUserButtonColor
  /** always = show name on all breakpoints; lg = name from lg up (header) */
  showLabel?: "always" | "lg"
  /** Show a trailing "Sign in" hint when logged out */
  showSignInAction?: boolean
  className?: string
  onClick?: () => void
}

const COLOR_STYLES: Record<
  AuthUserButtonColor,
  {
    root: string
    avatar: string
    icon: string
    title: string
    subtitle: string
    action: string
  }
> = {
  brand: {
    root: "border-primary/20 bg-primary/5 hover:bg-primary/10",
    avatar: "border-primary bg-primary/10 group-hover:bg-primary",
    icon: "text-primary group-hover:text-primary-foreground",
    title: "text-primary",
    subtitle: "text-primary/80",
    action: "text-primary",
  },
  white: {
    root: "border-white/30 bg-white/10 hover:bg-white/15",
    avatar: "border-white bg-white/10 group-hover:bg-white",
    icon: "text-white group-hover:text-black",
    title: "text-white",
    subtitle: "text-white/80",
    action: "text-white",
  },
}

export const AuthUserButton = observer(
  forwardRef<HTMLButtonElement, AuthUserButtonProps>(function AuthUserButton(
    {
      color = "brand",
      showLabel = "always",
      showSignInAction = false,
      className,
      onClick,
    },
    ref,
  ) {
    const { authStore } = useStore()
    const isAuthenticated = authStore.isAuthenticated
    const styles = COLOR_STYLES[color]

    const displayName = useMemo(() => {
      const fromProfile = [
        authStore.user?.profile?.firstName,
        authStore.user?.profile?.lastName,
      ]
        .filter(Boolean)
        .join(" ")
        .trim()
      if (fromProfile) return fromProfile
      return authStore.user?.email ?? "Signed in"
    }, [
      authStore.user?.profile?.firstName,
      authStore.user?.profile?.lastName,
      authStore.user?.email,
    ])

    const avatarSrc = useMemo(() => {
      const raw = authStore.user?.profile?.avatar
      return raw ? toProxyUploadUrl(raw) : ""
    }, [authStore.user?.profile?.avatar])

    const avatarInitials = useMemo(() => {
      const first = authStore.user?.profile?.firstName?.trim()?.[0]
      const last = authStore.user?.profile?.lastName?.trim()?.[0]
      if (first || last) return `${first ?? ""}${last ?? ""}`.toUpperCase()
      return (authStore.user?.email?.trim()?.[0] ?? "U").toUpperCase()
    }, [
      authStore.user?.profile?.firstName,
      authStore.user?.profile?.lastName,
      authStore.user?.email,
    ])

    const displayEmail = authStore.user?.email ?? ""

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          "group flex min-h-11 w-full items-center justify-between rounded-lg border px-3 py-2 text-left transition-colors",
          styles.root,
          className,
        )}
        onClick={onClick}
      >
        <span className="flex min-w-0 items-center gap-2">
          {isAuthenticated ? (
            <Avatar
              className={cn(
                "h-8 w-8 shrink-0 border-2 transition-colors",
                styles.avatar,
              )}
            >
              {avatarSrc ? (
                <AvatarImage src={avatarSrc} alt={displayName} />
              ) : null}
              <AvatarFallback className="text-xs">{avatarInitials}</AvatarFallback>
            </Avatar>
          ) : (
            <span
              className={cn(
                "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                styles.avatar,
              )}
            >
              <User className={cn("h-4 w-4 transition-colors", styles.icon)} />
            </span>
          )}
          <span
            className={cn(
              "min-w-0 flex-col",
              showLabel === "lg" ? "hidden lg:flex" : "flex",
            )}
          >
            {isAuthenticated ? (
              <>
                <span className={cn("block truncate text-sm font-bold", styles.title)}>
                  {displayName}
                </span>
                {displayEmail ? (
                  <span
                    className={cn(
                      "block truncate text-xs font-medium",
                      styles.subtitle,
                    )}
                  >
                    {displayEmail}
                  </span>
                ) : null}
              </>
            ) : (
              <span className={cn("block text-sm font-bold", styles.title)}>
                Log in / Sign up
              </span>
            )}
          </span>
        </span>
        {showSignInAction && !isAuthenticated ? (
          <span className={cn("shrink-0 text-xs font-semibold", styles.action)}>
            Sign in
          </span>
        ) : null}
      </button>
    )
  }),
)
